
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Loader2, Trophy } from "lucide-react";

export default function GolfVerification() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isVerifying, setIsVerifying] = useState(true);
  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registrationId, setRegistrationId] = useState<string | null>(null);
  const [paymentToken, setPaymentToken] = useState<string | null>(null);

  useEffect(() => {
    const id = searchParams.get("id");
    const token = searchParams.get("token");

    const verify = async () => {
      if (!id || !token) {
        setError("Paramètres manquants dans l'URL.");
        setIsVerifying(false);
        return;
      }

      try {
        // Verify the token
        const { data, error: rpcError } = await supabase
          .rpc("verify_golf_registration_token", {
            registration_id: id,
            token: token,
          } as any);

        if (rpcError) throw rpcError;

        const results = data as any[];
        if (!results || results.length === 0) {
          throw new Error("Lien de vérification invalide ou expiré.");
        }

        const result = results[0];
        if (result.payment_status === "paid" || result.payment_status === "completed") {
          setError("Cette inscription a déjà été payée.");
          setIsVerifying(false);
          return;
        }

        // Update status to pending_payment
        await supabase.rpc("update_golf_registration_status", {
          registration_id: id,
          token: token,
          new_status: "pending_payment",
        } as any);

        setIsVerified(true);
        setRegistrationId(id);
        setPaymentToken(token);
        toast({ title: "Email vérifié", description: "Vous allez être redirigé vers le paiement." });

        setTimeout(() => {
          navigate(`/golf-paiement?id=${id}&token=${token}`, { replace: true });
        }, 1500);
      } catch (err: any) {
        setError(err.message || "Erreur de vérification");
      } finally {
        setIsVerifying(false);
      }
    };

    verify();
  }, [searchParams, navigate, toast]);

  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center">
      {/* Background with gradient and gavroche */}
      <div className="absolute inset-0 z-0">
        <div className="h-full w-full bg-gradient-to-br from-[#FFFF4D] via-[#FFE94B] to-[#A3D960]"></div>
        <div className="absolute top-0 right-0 h-full w-1/3 md:w-2/5 opacity-30 md:opacity-40 flex items-end">
          <img 
            src="/lovable-uploads/180f51c0-05cf-4dfd-b4b7-91b153baf486.png" 
            alt="Décoration" 
            className="h-auto w-full object-contain object-bottom drop-shadow-xl" 
          />
        </div>
      </div>
      <div className="max-w-md w-full mx-4 relative z-10">
        <div className="text-center mb-6">
          <Trophy className="h-10 w-10 text-[#00509E] mx-auto mb-2" />
          <h1 className="text-xl font-bold text-[#001233]">Golf 2026</h1>
        </div>
        <div className="bg-white rounded-xl shadow-2xl p-6">
          <h2 className="text-xl font-bold text-center mb-6">Vérification de votre inscription</h2>
          {isVerifying ? (
            <div className="flex flex-col items-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-[#00509E] mb-4" />
              <p className="text-muted-foreground">Vérification en cours...</p>
            </div>
          ) : error ? (
            <div className="text-center py-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button variant="outline" onClick={() => navigate("/inscription-golf2026")}>
                Retour à l'inscription
              </Button>
            </div>
          ) : (
            <div className="text-center py-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-muted-foreground mb-4">Redirection vers le paiement...</p>
              <Button onClick={() => registrationId && navigate(`/golf-paiement?id=${registrationId}&token=${paymentToken}`)}>
                Procéder au paiement
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
